/**
 * AuthContext.js - Système d'authentification unifié
 * 
 * ARCHITECTURE:
 * - En preview (cross-domain): utilise localStorage token + Authorization header
 * - En production (same-domain): utilise cookies HttpOnly (plus sécurisé)
 * 
 * La détection est automatique basée sur le domaine.
 * Cette architecture permet une transition transparente vers la production.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CONSTANTES
// ============================================

const AUTH_STATE = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated'
};

const SESSION_TOKEN_KEY = 'abl_session_token';

// Détection automatique de l'environnement
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('preview.emergentagent.com') || 
         hostname.includes('emergentcf.cloud') ||
         hostname.includes('localhost');
};

// ============================================
// HELPERS TOKEN STORAGE
// ============================================

const getStoredToken = () => {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    // IMPORTANT: Concaténation uniquement, pas de virgules ni template literals
    const tokenDisplay = token ? String(token).substring(0, 25) + '...' : 'null';
    console.log('[AUTH] getStoredToken: ' + tokenDisplay);
    return token;
  } catch (e) {
    // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
    console.log('[AUTH] localStorage read error: ' + String(e?.message || e));
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
      // IMPORTANT: Concaténation uniquement, pas de virgules ni template literals
      const tokenPrefix = String(token).substring(0, 25);
      console.log('[AUTH] Token STORED in localStorage: ' + tokenPrefix + '...');
      // Verify it was stored
      const verify = localStorage.getItem(SESSION_TOKEN_KEY);
      console.log('[AUTH] Token VERIFIED: ' + (verify === token ? 'OK' : 'MISMATCH!'));
    } else {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      console.log('[AUTH] Token REMOVED from localStorage');
    }
  } catch (e) {
    // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
    console.log('[AUTH] localStorage write error: ' + String(e?.message || e));
  }
};

// ============================================
// HELPER: NETTOYAGE DONNÉES UTILISATEUR
// ============================================

const cleanUserData = (data) => {
  if (!data) return null;
  
  const cleaned = {
    user_id: String(data.user_id || ''),
    email: String(data.email || ''),
    name: String(data.name || ''),
    picture: data.picture ? String(data.picture) : null,
    subscription_plan: String(data.subscription_plan || 'free'),
    terms_accepted: Boolean(data.terms_accepted),
    tutorials_enabled: data.tutorials_enabled !== false
  };
  
  if (data.usage && typeof data.usage === 'object') {
    cleaned.usage = {
      quotes_analyzed: Number(data.usage.quotes_analyzed || 0),
      quotes_compared: Number(data.usage.quotes_compared || 0),
      estimates_created: Number(data.usage.estimates_created || 0),
      recipes_created: Number(data.usage.recipes_created || 0),
      chat_messages: Number(data.usage.chat_messages || 0)
    };
  }
  
  return cleaned;
};

// ============================================
// AUTH PROVIDER
// ============================================

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [authState, setAuthState] = useState(AUTH_STATE.LOADING);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [tutorialsEnabled, setTutorialsEnabledState] = useState(true);
  
  // Prevent multiple simultaneous auth checks
  const authCheckInProgress = useRef(false);
  const initialCheckDone = useRef(false);

  const setUser = useCallback((data) => {
    setUserState(cleanUserData(data));
  }, []);

  const loading = authState === AUTH_STATE.LOADING;

  // ============================================
  // CHECK AUTH - Logique unifiée
  // ============================================
  
  const checkAuth = useCallback(async (forceCheck = false) => {
    const timestamp = Date.now();
    // IMPORTANT: Utiliser concaténation de strings pour éviter DataCloneError
    console.log('[AUTH ' + timestamp + '] checkAuth called, forceCheck=' + forceCheck + ', path=' + window.location.pathname);
    
    // Skip si OAuth callback en cours
    if (window.location.hash?.includes('session_id=')) {
      console.log('[AUTH ' + timestamp + '] OAuth callback detected, skipping check');
      return;
    }

    // Eviter les appels simultanés
    if (authCheckInProgress.current) {
      console.log('[AUTH ' + timestamp + '] Check already in progress, skipping');
      return;
    }
    
    // Skip si déjà vérifié (sauf si forcé)
    if (initialCheckDone.current && !forceCheck) {
      console.log('[AUTH ' + timestamp + '] Already checked (initialCheckDone=true), skipping');
      return;
    }

    authCheckInProgress.current = true;
    console.log('[AUTH ' + timestamp + '] === STARTING AUTH CHECK ===');
    console.log('[AUTH ' + timestamp + '] Environment: ' + (isPreviewEnvironment() ? 'PREVIEW' : 'PRODUCTION'));
    console.log('[AUTH ' + timestamp + '] API URL: ' + API_URL);
    
    const storedToken = getStoredToken();
    
    // SI PAS DE TOKEN ET PAS DE COOKIE ATTENDU → pas besoin d'appeler l'API
    if (!storedToken && isPreviewEnvironment()) {
      console.log('[AUTH ' + timestamp + '] No token in preview environment → UNAUTHENTICATED');
      setAuthState(AUTH_STATE.UNAUTHENTICATED);
      initialCheckDone.current = true;
      authCheckInProgress.current = false;
      return;
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      // Construction des headers
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (storedToken) {
        headers['Authorization'] = 'Bearer ' + storedToken;
        console.log('[AUTH ' + timestamp + '] Added Authorization header');
      }
      
      console.log('[AUTH ' + timestamp + '] Calling ' + API_URL + '/api/auth/me...');
      
      const response = await fetch(API_URL + '/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      // IMPORTANT: Concaténation de strings uniquement pour éviter DataCloneError
      console.log('[AUTH ' + timestamp + '] Response status: ' + response.status);
      
      if (response.ok) {
        const userData = await response.json();
        const safeEmail = String(userData?.email || 'unknown');
        console.log('[AUTH ' + timestamp + '] ✓ AUTHENTICATED: ' + safeEmail);
        // IMPORTANT: Nettoyer les données pour éviter DataCloneError
        const cleanedData = cleanUserData(userData);
        setUser(cleanedData);
        setTermsAccepted(cleanedData?.terms_accepted || false);
        setTutorialsEnabledState(cleanedData?.tutorials_enabled !== false);
        setAuthState(AUTH_STATE.AUTHENTICATED);
        initialCheckDone.current = true;
        console.log('[AUTH ' + timestamp + '] State set to AUTHENTICATED');
      } else if (response.status === 401) {
        console.log('[AUTH ' + timestamp + '] ✗ 401 Unauthorized');
        const errorText = await response.text();
        console.log('[AUTH ' + timestamp + '] Error response: ' + String(errorText || 'empty'));
        setUser(null);
        setStoredToken(null);
        setAuthState(AUTH_STATE.UNAUTHENTICATED);
        initialCheckDone.current = true;
        console.log('[AUTH ' + timestamp + '] State set to UNAUTHENTICATED, token cleared');
      } else {
        console.log('[AUTH ' + timestamp + '] Unexpected status: ' + response.status);
        throw new Error('HTTP ' + response.status);
      }
    } catch (error) {
      // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
      const errName = String(error?.name || 'UnknownError');
      const errMsg = String(error?.message || error || 'Unknown error');
      console.log('[AUTH ' + timestamp + '] ERROR: ' + errName + ' - ' + errMsg);
      if (errName === 'AbortError') {
        console.log('[AUTH ' + timestamp + '] Request timed out');
      }
      setUser(null);
      setAuthState(AUTH_STATE.UNAUTHENTICATED);
      initialCheckDone.current = true;
      console.log('[AUTH ' + timestamp + '] State set to UNAUTHENTICATED due to error');
    } finally {
      authCheckInProgress.current = false;
      console.log('[AUTH ' + timestamp + '] === AUTH CHECK COMPLETE ===');
    }
  }, [setUser]);

  // Check auth au montage
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ============================================
  // AUTH HEADERS - Pour les requêtes API
  // ============================================
  
  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    const storedToken = getStoredToken();
    
    // CRITICAL LOG - Always log for debugging (concaténation uniquement)
    const tokenStatus = storedToken ? 'YES (' + String(storedToken).substring(0, 20) + '...)' : 'NO';
    console.log('[AUTH HEADERS] Token present: ' + tokenStatus);
    
    if (storedToken) {
      headers['Authorization'] = 'Bearer ' + storedToken;
      console.log('[AUTH HEADERS] ✓ Authorization header ADDED to request');
    } else {
      console.log('[AUTH HEADERS] ⚠ NO TOKEN - Authorization header NOT added');
    }
    return headers;
  }, []);

  // ============================================
  // LOGIN - Google OAuth
  // ============================================
  
  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    console.log('[AUTH] Initiating Google login, redirect: ' + redirectUrl);
    window.location.href = 'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
  };

  // ============================================
  // LOGOUT
  // ============================================
  
  const logout = async () => {
    console.log('[AUTH] Logging out...');
    try {
      const headers = getAuthHeaders();
      await fetch(API_URL + '/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (error) {
      // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
      console.log('[AUTH] Logout error: ' + String(error?.message || error));
    } finally {
      // Clear all auth state
      setUser(null);
      setStoredToken(null);
      setTermsAccepted(false);
      setTutorialsEnabledState(true);
      setAuthState(AUTH_STATE.UNAUTHENTICATED);
      initialCheckDone.current = false;
      window.location.href = '/';
    }
  };

  // ============================================
  // DEMO LOGIN
  // ============================================
  
  const demoLogin = async () => {
    console.log('[AUTH] Starting demo login...');
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await fetch(API_URL + '/api/auth/demo_login', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.session_token) {
          setStoredToken(data.session_token);
        }
        console.log('[AUTH] Demo login successful, redirecting...');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
      console.log('[AUTH] Demo login error: ' + String(error?.message || error));
    }
  };

  // ============================================
  // STORE TOKEN - Exposé pour le login email
  // ============================================
  
  const storeToken = useCallback((token) => {
    setStoredToken(token);
  }, []);

  // ============================================
  // REFRESH AUTH - Force une re-vérification
  // ============================================
  
  const refreshAuth = useCallback(async () => {
    console.log('[AUTH] Forcing auth refresh...');
    initialCheckDone.current = false;
    await checkAuth(true);
  }, [checkAuth]);

  // ============================================
  // SET AUTH DIRECTLY - Pour login email
  // ============================================
  
  const setAuthenticatedUser = useCallback((userData, token) => {
    // IMPORTANT: Ne jamais passer d'objets à console.log (DataCloneError sur certains navigateurs)
    const safeEmail = String(userData?.email || 'unknown');
    console.log('[AUTH] Setting authenticated user directly: ' + safeEmail);
    if (token) {
      setStoredToken(token);
    }
    // IMPORTANT: Nettoyer les données utilisateur pour éviter DataCloneError
    const cleanedData = cleanUserData(userData);
    setUser(cleanedData);
    setAuthState(AUTH_STATE.AUTHENTICATED);
    initialCheckDone.current = true;
  }, [setUser]);

  // ============================================
  // TUTORIALS PREFERENCE
  // ============================================
  
  const setTutorialsEnabled = async (enabled) => {
    try {
      const response = await fetch(API_URL + '/api/account/tutorials', {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ tutorials_enabled: enabled })
      });
      
      if (response.ok) {
        setTutorialsEnabledState(enabled);
        if (user) {
          setUser({ ...user, tutorials_enabled: enabled });
        }
        return true;
      }
      return false;
    } catch (error) {
      // Ne jamais passer l'objet Error directement (non-cloneable pour postMessage)
      console.log('[AUTH] Error updating tutorials: ' + String(error?.message || error));
      return false;
    }
  };

  // ============================================
  // TERMS ACCEPTANCE
  // ============================================
  
  const acceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  const value = {
    // User state
    user,
    setUser,
    loading,
    authState,
    isAuthenticated: authState === AUTH_STATE.AUTHENTICATED,
    isPremium: user?.subscription_plan === 'premium',
    
    // Auth actions
    login,
    logout,
    demoLogin,
    checkAuth,
    refreshAuth,
    storeToken,
    setAuthenticatedUser,
    getAuthHeaders,
    
    // Terms & tutorials
    termsAccepted,
    setTermsAccepted,
    showTermsModal,
    setShowTermsModal,
    acceptTerms,
    tutorialsEnabled,
    setTutorialsEnabled
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Export AUTH_STATE pour les autres composants
export { AUTH_STATE };

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
